import Fuse from 'fuse.js';
import { isValid } from 'date-fns';

import type { Ref } from 'vue';
import type { Customer } from '@/_api/models/customer';
import {
  CustomerPrivacyConsent,
  CustomerPrivacyConsentLabels,
  CustomerMarketingConsent,
  CustomerMarketingConsentLabels,
  CustomerMassEmail,
  CustomerMassEmailLabels,
  CustomerMassSMS,
  CustomerMassSMSLabels,
} from '@/_api/models/customer';
import { EmployeeType, EmployeeTypeLabels } from '@/_api/models/employee';
import type { FilterConfig, FilterState } from '@/_api/types/interfaces/filter';

import DateRangePresets from '@/components/fields/presets/DateRange';

import { useFormStore } from '~/stores/useFormStore';
import { useEmployeeStore } from '~/stores/useEmployeeStore';
import { useServiceStore } from '~/stores/useServiceStore';
import { useSegmentStore } from '~/stores/useSegmentStore';
import { useProductStore } from '~/stores/useProductStore';
import {
  customerMatchesNameSearch,
  mergeCustomerSearchResults,
} from '~/lib/customerSearch';

import { useFilterController, type FilterControllerReturn } from '../useFilterController';

// --- Helpers ---

const constructLabel = (value: any[], key: string, $i18n: any): string => {
  const labels = value.slice(0, 3).map((item) => item[key]).join($i18n.t('general.listSeparator'));
  if (value.length > 3) {
    return $i18n.t('general.andMore', { list: labels, count: value.length - 3 });
  }
  return labels;
};

const dateRangeValueLabel = (value: { start: any; finish: any }, $date: any): string => {
  const start = value.start ? new Date(value.start * 1000) : null;
  const finish = value.finish ? new Date(value.finish * 1000) : null;
  return (
    (start ? $date.format(start, $date.presets.short) : '') +
    ' » ' +
    (finish ? $date.format(finish, $date.presets.short) : '')
  );
};

const dateRangeIsEmpty = (value: { start: any; finish: any }): boolean => {
  return !value.start && !value.finish;
};

const dateRangeGetParams = (value: { start: any; finish: any }): Record<string, any> => {
  return { from: value.start, to: value.finish };
};

const dateRangeDefaultValue = () => ({ start: null, finish: null });

const fieldValue = (value: any): any => {
  if (value && typeof value === 'object' && 'value' in value) {
    return fieldValue(value.value);
  }
  return value;
};

const numericEnumValue = (value: any): number | null => {
  const raw = fieldValue(value);
  if (raw == null || raw === '') return null;
  if (typeof raw === 'boolean') return raw ? 1 : 0;
  if (typeof raw === 'string' && raw.trim() !== '' && !Number.isNaN(Number(raw))) {
    return Number(raw);
  }
  return typeof raw === 'number' ? raw : null;
};

const consentEnumValue = (value: any): CustomerPrivacyConsent | CustomerMarketingConsent | null => {
  const raw = fieldValue(value);
  if (raw == null || raw === 'null') return CustomerPrivacyConsent.notObtained;
  if (raw === true || raw === 'true' || raw === 1 || raw === '1') return CustomerPrivacyConsent.accepted;
  if (raw === false || raw === 'false' || raw === 0 || raw === '0') return CustomerPrivacyConsent.declined;
  return null;
};

export interface CustomerFilterReturn extends FilterControllerReturn<Customer> {
  updateFormOptions: () => void;
  updateEmployeeOptions: () => void;
  updateServiceOptions: () => void;
  updateSegmentOptions: () => void;
  updateProductOptions: () => void;
  updatePassOptions: () => Promise<void>;
}

export const useCustomerFilters = (): CustomerFilterReturn => {
  const { $api, $features, $i18n, $date } = useNuxtApp() as any;

  // Deferred reference - assigned after useFilterController() returns.
  // All closures that read `states` are called lazily (at render time / after setup),
  // so `states` will always be assigned by the time they execute.
  let states: Ref<Record<string, FilterState>>;
  let configs: Record<string, FilterConfig<Customer>>;

  const controller = useFilterController<Customer>({
    service: $api.customer,
    matchAll: true,
    customFields: { modelName: 'customer' },

    filters: {
      // ==========================================
      // LOCAL FILTERS
      // ==========================================

      hasEmail: {
        type: 'hasEmail',
        label: $i18n.t('general.email'),
        icon: 'lucide:mail',
        component: 'RadioGroupField',
        strategy: 'local',
        options: [
          { label: $i18n.t('customer.filters.hasEmail'), value: 'true' },
          { label: $i18n.t('customer.filters.hasNoEmail'), value: 'false' },
        ],
        props: { outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2' },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) => (value === 'true' ? $i18n.t('general.yes') : $i18n.t('general.no')),
        matches: (customers, value) => {
          if (value === 'true') return customers.filter((c) => c.email && c.email.length > 0);
          if (value === 'false') return customers.filter((c) => !c.email);
          return customers;
        },
      },

      hasPhone: {
        type: 'hasPhone',
        label: $i18n.t('customer.filters.phone'),
        icon: 'lucide:phone',
        component: 'RadioGroupField',
        strategy: 'local',
        options: [
          { label: $i18n.t('customer.filters.hasPhone'), value: 'true' },
          { label: $i18n.t('customer.filters.hasNoPhone'), value: 'false' },
        ],
        props: { outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2' },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) => (value === 'true' ? $i18n.t('general.yes') : $i18n.t('general.no')),
        matches: (customers, value) => {
          if (value === 'true') return customers.filter((c) => c.phone && c.phone.length > 0);
          if (value === 'false') return customers.filter((c) => !c.phone);
          return customers;
        },
      },

      gender: {
        type: 'gender',
        label: $i18n.t('general.gender'),
        icon: 'lucide:user',
        component: 'SelectField',
        strategy: 'local',
        options: [
          { label: $i18n.t('customer.gender.none'), value: '-' },
          { label: $i18n.t('customer.gender.female'), value: 'F' },
          { label: $i18n.t('customer.gender.male'), value: 'M' },
          { label: $i18n.t('customer.gender.nonBinary'), value: 'NB' },
        ],
        props: { placeholder: $i18n.t('general.any') },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) => {
          if (value === '-') return $i18n.t('general.none');
          if (value === 'F') return $i18n.t('customer.gender.female');
          if (value === 'M') return $i18n.t('customer.gender.male');
          if (value === 'NB') return $i18n.t('customer.gender.nonBinary');
          return null;
        },
        matches: (customers, value) => {
          return customers.filter((customer: any) => {
            if (value === '-') return !customer.gender;
            return customer.gender === value;
          });
        },
      },

      dateOfBirth: {
        type: 'dateOfBirth',
        label: $i18n.t('general.dateOfBirth'),
        icon: 'lucide:cake',
        component: 'DateOfBirthFilter',
        strategy: 'local',
        defaultValue: { day: null, month: null, year: null, hasDob: null },
        isEmpty: (value) =>
          value.day == null && value.month == null && value.year == null && value.hasDob == null,
        validate: (value) => {
          const { year, month, day } = value;
          const valid = isValid(new Date(year || 0, month || 0, day || 1));
          return valid ? null : { status: false, message: $i18n.t('customer.filters.invalidDate') };
        },
        valueLabel: (value) => {
          if (value.day || value.month || value.year) {
            const format = [];
            const { year, month, day } = value;
            if (day != null) format.push('do');
            if (month != null) format.push('MMM');
            if (year != null) format.push('yyyy');
            return $date.format(new Date(year || 0, month || 0, day || 1), format.join(' '));
          }
          return null;
        },
        matches: (customers, value) => {
          if (value.hasDob === 'false') {
            return customers.filter((customer: any) => !customer.dateOfBirth?.value);
          }
          const { year, day } = value;
          let { month } = value;
          month = month !== null ? month + 1 : null;
          return customers.filter(({ dateOfBirth }: any) => {
            if (!dateOfBirth?.value) return false;
            const date = dateOfBirth.value.toString();
            const c = {
              year: Number(date.substr(0, 4)),
              month: Number(date.substr(4, 2)),
              day: Number(date.substr(6, 2)),
            };
            return (
              (day ? day === c.day : true) &&
              (month ? month === c.month : true) &&
              (year ? year === c.year : true)
            );
          });
        },
      },

      hasPhoto: {
        type: 'hasPhoto',
        label: $i18n.t('customer.filters.hasPhoto'),
        icon: 'lucide:image',
        component: 'SelectField',
        strategy: 'local',
        info: $i18n.t('customer.filters.hasPhotoInfo'),
        options: [
          { label: $i18n.t('customer.filters.hasPhoto'), value: 'true' },
          { label: $i18n.t('customer.filters.hasNoPhoto'), value: 'false' },
        ],
        props: { placeholder: $i18n.t('general.any') },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) => (value === 'true' ? $i18n.t('general.yes') : $i18n.t('general.no')),
        matches: (customers, value) => {
          const has = value === 'true';
          return customers.filter((c: any) => (has ? c.profilePhoto != null : c.profilePhoto == null));
        },
      },

      uploads: {
        type: 'uploads',
        label: $i18n.t('customer.filters.uploads'),
        icon: 'lucide:upload',
        info: $i18n.t('customer.filters.uploadsInfo'),
        component: 'SelectField',
        strategy: 'local',
        props: {
          placeholder: $i18n.t('general.any'),
        },
        options: [
          { label: $i18n.t('customer.filters.noUploads'), value: 'noUploads' },
          { label: $i18n.t('customer.filters.hasAnyUploads'), value: 'hasAnyUploads' },
          { label: $i18n.t('customer.filters.hasPhotos'), value: 'hasPhotos' },
          { label: $i18n.t('customer.filters.hasDocuments'), value: 'hasDocuments' },
          { label: $i18n.t('customer.filters.hasNoPhotos'), value: 'hasNoPhotos' },
          { label: $i18n.t('customer.filters.hasNoDocuments'), value: 'hasNoDocuments' },
        ],
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) => {
          const option = [
            { label: $i18n.t('customer.filters.noUploads'), value: 'noUploads' },
            { label: $i18n.t('customer.filters.hasAnyUploads'), value: 'hasAnyUploads' },
            { label: $i18n.t('customer.filters.hasPhotos'), value: 'hasPhotos' },
            { label: $i18n.t('customer.filters.hasDocuments'), value: 'hasDocuments' },
            { label: $i18n.t('customer.filters.hasNoPhotos'), value: 'hasNoPhotos' },
            { label: $i18n.t('customer.filters.hasNoDocuments'), value: 'hasNoDocuments' },
          ].find((item) => item.value === value)
          return option?.label || null
        },
        matches: (customers, value) => {
          return customers.filter((customer: any) => {
            const uploadPhotoCount = Number(customer?.stats?.uploadPhotoCount || 0);
            const uploadDocumentCount = Number(customer?.stats?.uploadDocumentCount || 0);
            const totalUploads = uploadPhotoCount + uploadDocumentCount;

            if (value === 'noUploads') return totalUploads <= 0;
            if (value === 'hasAnyUploads') return totalUploads > 0;
            if (value === 'hasPhotos') return uploadPhotoCount > 0;
            if (value === 'hasDocuments') return uploadDocumentCount > 0;
            if (value === 'hasNoPhotos') return uploadPhotoCount <= 0;
            if (value === 'hasNoDocuments') return uploadDocumentCount <= 0;

            return true;
          });
        },
      },

      allowOnlineBooking: {
        type: 'allowOnlineBooking',
        label: $i18n.t('general.onlineBooking'),
        icon: 'lucide:globe',
        component: 'RadioGroupField',
        strategy: 'local',
        options: [
          { label: $i18n.t('general.allowed'), value: 'true' },
          { label: $i18n.t('general.notAllowed'), value: 'false' },
        ],
        props: { outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2' },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) => (value === 'true' ? $i18n.t('general.yes') : $i18n.t('general.no')),
        matches: (customers, value) => {
          if (value === 'true') return customers.filter((c: any) => c.allowOnlineBooking);
          if (value === 'false') return customers.filter((c: any) => !c.allowOnlineBooking);
          return customers;
        },
      },

      loyaltyPoints: {
        type: 'loyaltyPoints',
        label: $i18n.t('general.loyaltyPoints'),
        icon: 'lucide:star',
        component: 'RadioGroupField',
        strategy: 'local',
        options: [
          { label: $i18n.t('customer.filters.hasLoyaltyPoints'), value: 'true' },
          { label: $i18n.t('customer.filters.noLoyaltyPoints'), value: 'false' },
        ],
        props: { outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2' },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) => (value === 'true' ? $i18n.t('general.yes') : $i18n.t('general.no')),
        matches: (customers, value) => {
          if (value === 'true') return customers.filter((c: any) => c.loyaltyPointBalance > 0);
          if (value === 'false') return customers.filter((c: any) => c.loyaltyPointBalance <= 0);
          return customers;
        },
      },

      segments: {
        type: 'segments',
        label: $i18n.t('general.segments'),
        icon: 'lucide:tag',
        // `kind: 'list'` makes FacetedFilter render its built-in
        // `ListPicker` (searchable multi-select). `options` arrives
        // via `updateSegmentOptions()` after the store hydrates.
        kind: 'list',
        strategy: 'local',
        defaultValue: [],
        isEmpty: (value) => !Array.isArray(value) || value.length === 0,
        valueLabel: (value) => {
          if (!Array.isArray(value) || !value.length) return null;
          return constructLabel(value, 'name', $i18n);
        },
        matches: (customers, value) => {
          if (!Array.isArray(value)) return customers;
          const selected = value.map(({ idSegment }: any) => idSegment);
          return customers.filter((customer: any) => {
            const ids = customer.segments.map(({ idSegment }: any) => idSegment);
            return ids.some((item: any) => selected.includes(item));
          });
        },
      },

      emailBounced: {
        type: 'emailBounced',
        label: $i18n.t('general.emailBounced'),
        icon: 'lucide:mail-x',
        info: $i18n.t('customer.filters.emailBouncedInfo'),
        // FacetedFilter renders a built-in USwitch for boolean filters.
        kind: 'boolean',
        strategy: 'local',
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: () => $i18n.t('general.yes'),
        matches: (customers) => customers.filter((c: any) => c.emailBounced),
      },

      privacyConsent: {
        type: 'privacyConsent',
        label: $i18n.t('general.privacyConsent'),
        icon: 'lucide:shield-check',
        component: 'RadioGroupField',
        strategy: 'local',
        options: [
          { label: $i18n.t('customer.consent.accepted'), value: 'accepted' },
          { label: $i18n.t('customer.consent.declined'), value: 'declined' },
          { label: $i18n.t('customer.consent.notObtained'), value: 'notObtained' },
        ],
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) =>
          $i18n.t(CustomerPrivacyConsentLabels[value as keyof typeof CustomerPrivacyConsentLabels]),
        matches: (customers, value) =>
          customers.filter(
            (c: any) => consentEnumValue(c.consentAccepted) === CustomerPrivacyConsent[value as keyof typeof CustomerPrivacyConsent],
          ),
      },

      marketingConsent: {
        type: 'marketingConsent',
        label: $i18n.t('general.marketingConsent'),
        icon: 'lucide:megaphone',
        component: 'RadioGroupField',
        strategy: 'local',
        options: [
          { label: $i18n.t('customer.consent.accepted'), value: 'accepted' },
          { label: $i18n.t('customer.consent.declined'), value: 'declined' },
          { label: $i18n.t('customer.consent.notObtained'), value: 'notObtained' },
        ],
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) =>
          $i18n.t(CustomerMarketingConsentLabels[value as keyof typeof CustomerMarketingConsentLabels]),
        matches: (customers, value) =>
          customers.filter(
            (c: any) =>
              consentEnumValue(c.marketingConsentAccepted) ===
              CustomerMarketingConsent[value as keyof typeof CustomerMarketingConsent],
          ),
      },

      massEmailSubscription: {
        type: 'massEmailSubscription',
        label: $i18n.t('customer.filters.massEmailSubscription'),
        icon: 'lucide:mail-check',
        component: 'RadioGroupField',
        strategy: 'local',
        options: [
          { label: $i18n.t('general.subscribed'), value: 'subscribed' },
          { label: $i18n.t('general.unsubscribed'), value: 'unsubscribed' },
        ],
        props: { outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2' },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) =>
          $i18n.t(CustomerMassEmailLabels[CustomerMassEmail[value as keyof typeof CustomerMassEmail]]),
        matches: (customers, value) =>
          customers.filter(
            (c: any) => numericEnumValue(c.massEmailUnsubscribed) === CustomerMassEmail[value as keyof typeof CustomerMassEmail],
          ),
      },

      massSMSSubscription: {
        type: 'massSMSSubscription',
        label: $i18n.t('customer.filters.massSMSSubscription'),
        icon: 'lucide:message-circle',
        component: 'RadioGroupField',
        strategy: 'local',
        options: [
          { label: $i18n.t('general.subscribed'), value: 'subscribed' },
          { label: $i18n.t('general.unsubscribed'), value: 'unsubscribed' },
        ],
        props: { outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2' },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) =>
          $i18n.t(CustomerMassSMSLabels[CustomerMassSMS[value as keyof typeof CustomerMassSMS]]),
        matches: (customers, value) =>
          customers.filter(
            (c: any) => numericEnumValue(c.massSMSUnsubscribed) === CustomerMassSMS[value as keyof typeof CustomerMassSMS],
          ),
      },

      search: {
        type: 'search',
        label: $i18n.t('general.search'),
        icon: 'lucide:search',
        strategy: 'local',
        force: true,
        defaultValue: null,
        isEmpty: (value) => !value,
        matches: (customers, value) => {
          const nameMatches = customers.filter((customer) =>
            customerMatchesNameSearch(customer, value),
          );
          const fuse = new Fuse(customers, {
            keys: [
              { name: 'displayName', weight: 2 },
              { name: 'firstName', weight: 2 },
              { name: 'lastName', weight: 2 },
              'email', 'phone', 'address', 'city', 'postcode',
              'suburb', 'region', 'homePhone', 'company',
            ],
            threshold: 0.3,
          });
          return mergeCustomerSearchResults(
            nameMatches,
            fuse.search(value).map(({ item }) => item),
          );
        },
      },

      // ==========================================
      // REMOTE FILTERS
      // ==========================================

      created: {
        type: 'created',
        label: $i18n.t('general.created'),
        icon: 'lucide:calendar-plus',
        info: $i18n.t('customer.filters.createdInfo'),
        component: 'DateRangeFilter',
        strategy: 'remote',
        options: [
          DateRangePresets.today, DateRangePresets.oneWeek, DateRangePresets.twoWeeks,
          DateRangePresets.thisMonth, DateRangePresets.lastMonth,
        ],
        defaultValue: dateRangeDefaultValue(),
        isEmpty: dateRangeIsEmpty,
        valueLabel: (value) => dateRangeValueLabel(value, $date),
        getParams: dateRangeGetParams,
      },

      inactive: {
        type: 'inactive',
        label: $i18n.t('general.inactive'),
        icon: 'lucide:clock',
        info: $i18n.t('customer.filters.inactiveInfo'),
        component: 'DateRangeFilter',
        strategy: 'remote',
        options: [
          DateRangePresets.today, DateRangePresets.oneWeek, DateRangePresets.twoWeeks,
          DateRangePresets.thisMonth, DateRangePresets.lastMonth,
          { ...DateRangePresets.future, name: $i18n.t('customer.filters.noFutureAppointments') },
          { ...DateRangePresets.past, name: $i18n.t('customer.filters.noPastAppointments') },
        ],
        defaultValue: dateRangeDefaultValue(),
        isEmpty: dateRangeIsEmpty,
        valueLabel: (value) => dateRangeValueLabel(value, $date),
        getParams: dateRangeGetParams,
      },

      active: {
        type: 'active',
        label: $i18n.t('general.active'),
        icon: 'lucide:activity',
        info: $i18n.t('customer.filters.activeInfo'),
        component: 'DateRangeFilter',
        strategy: 'remote',
        options: [
          DateRangePresets.today, DateRangePresets.oneWeek, DateRangePresets.twoWeeks,
          DateRangePresets.thisMonth, DateRangePresets.lastMonth,
          { ...DateRangePresets.future, name: $i18n.t('customer.filters.hasFutureAppointments') },
          { ...DateRangePresets.past, name: $i18n.t('customer.filters.hasPastAppointments') },
        ],
        defaultValue: dateRangeDefaultValue(),
        isEmpty: dateRangeIsEmpty,
        valueLabel: (value) => dateRangeValueLabel(value, $date),
        getParams: dateRangeGetParams,
      },

      noShows: {
        type: 'no-shows',
        label: $i18n.t('customer.filters.noShows'),
        icon: 'lucide:user-x',
        info: $i18n.t('customer.filters.noShowsInfo'),
        component: 'DateRangeFilter',
        strategy: 'remote',
        options: [
          DateRangePresets.today, DateRangePresets.oneWeek, DateRangePresets.twoWeeks,
          DateRangePresets.thisMonth, DateRangePresets.lastMonth,
        ],
        defaultValue: dateRangeDefaultValue(),
        isEmpty: dateRangeIsEmpty,
        valueLabel: (value) => dateRangeValueLabel(value, $date),
        getParams: dateRangeGetParams,
      },

      cancellations: {
        type: 'cancellations',
        label: $i18n.t('customer.filters.cancellations'),
        icon: 'lucide:x-circle',
        info: $i18n.t('customer.filters.cancellationsInfo'),
        component: 'DateRangeFilter',
        strategy: 'remote',
        options: [
          DateRangePresets.today, DateRangePresets.oneWeek, DateRangePresets.twoWeeks,
          DateRangePresets.thisMonth, DateRangePresets.lastMonth,
        ],
        defaultValue: dateRangeDefaultValue(),
        isEmpty: dateRangeIsEmpty,
        valueLabel: (value) => dateRangeValueLabel(value, $date),
        getParams: dateRangeGetParams,
      },

      employees: {
        type: 'reservations',
        label: $i18n.t('general.employees'),
        icon: 'lucide:users',
        info: $i18n.t('customer.filters.employeesInfo'),
        component: 'EmployeesFilter',
        strategy: 'remote',
        watch: ['date'],
        options: {
          employees: {
            key: 'employees',
            component: 'CheckboxGroupField',
            label: $i18n.t('customer.filters.with'),
            visible: true,
            search: true,
            valueLabel: () => {
              const value = states?.value?.employees?.value?.employees || [];
              return value.length
                ? value.map(({ displayName }: any) => displayName).join($i18n.t('general.listSeparator'))
                : $i18n.t('customer.filters.allEmployees');
            },
            items: [],
          },
        },
        defaultValue: { employees: [], date: { start: null, finish: null } },
        isEmpty: (value) => !value.date.start && !value.date.finish && !value.employees.length,
        valueLabel: (value) => {
          const start = value.date.start ? new Date(value.date.start * 1000) : null;
          const finish = value.date.finish ? new Date(value.date.finish * 1000) : null;
          const date = start || finish
            ? `(${start ? $date.format(start, $date.presets.short) : ''} » ${finish ? $date.format(finish, $date.presets.short) : ''})`
            : '';
          const empLabel = states?.value?.employees?.options?.employees?.valueLabel?.() ?? $i18n.t('customer.filters.allEmployees');
          return `${empLabel} ${date}`;
        },
        getParams: (value) => ({
          employees: value.employees.map(({ idEmployee }: any) => ({ id: idEmployee })),
          from: value.date.start,
          to: value.date.finish,
        }),
      },

      services: {
        type: 'reservations',
        label: $i18n.t('general.services'),
        icon: 'lucide:scissors',
        info: $i18n.t('customer.filters.servicesInfo'),
        // Nuxt-UI-built widget; replaces the legacy `FieldsWithDateRange`
        // which didn't survive the move into the FacetedFilter popover.
        // The value shape is unchanged so `matches()` / remote params
        // (defined elsewhere on this filter) keep working as-is.
        component: 'ServicesFilter',
        strategy: 'remote',
        watch: ['date'],
        options: {
          services: {
            key: 'services',
            component: 'CheckboxCategoriesField',
            label: $i18n.t('general.services'),
            visible: false,
            search: true,
            selectCategory: true,
            valueLabel: () => {
              const value = states?.value?.services?.value?.services || [];
              if (value.length) return constructLabel(value, 'title', $i18n);
              return $i18n.t('customer.filters.allServices');
            },
            items: [],
          },
          employees: {
            key: 'employees',
            component: 'CheckboxGroupField',
            label: $i18n.t('customer.filters.with'),
            visible: false,
            search: true,
            valueLabel: () => {
              const value = states?.value?.services?.value?.employees || [];
              if (value.length) return constructLabel(value, 'title', $i18n);
              return $i18n.t('customer.filters.allEmployees');
            },
            items: [],
          },
        },
        defaultValue: { services: [], employees: [], date: { start: null, finish: null } },
        isEmpty: (value) =>
          !value.date.start && !value.date.finish && !value.services.length && !value.employees.length,
        valueLabel: (value) => {
          const start = value.date.start ? new Date(value.date.start * 1000) : null;
          const finish = value.date.finish ? new Date(value.date.finish * 1000) : null;
          const date = start || finish
            ? `(${start ? $date.format(start, $date.presets.short) : ''} » ${finish ? $date.format(finish, $date.presets.short) : ''})`
            : '';
          const svcLabel = states?.value?.services?.options?.services?.valueLabel?.() ?? $i18n.t('customer.filters.allServices');
          const empLabel = states?.value?.services?.options?.employees?.valueLabel?.() ?? $i18n.t('customer.filters.allEmployees');
          return `${$i18n.t('customer.filters.servicesWithEmployees', { services: svcLabel, employees: empLabel })} ${date}`;
        },
        getParams: (value) => ({
          services: value.services.map(({ idService }: any) => ({ id: idService })),
          employees: value.employees.map(({ idEmployee }: any) => ({ id: idEmployee })),
          from: value.date.start,
          to: value.date.finish,
        }),
      },

      duplicate: {
        type: 'duplicates',
        label: $i18n.t('general.duplicate'),
        icon: 'lucide:copy',
        info: $i18n.t('customer.filters.duplicateInfo'),
        kind: 'boolean',
        strategy: 'remote',
        defaultValue: null,
        isEmpty: (value) => !value,
        getParams: () => ({}),
      },

      archived: {
        type: 'archived',
        label: $i18n.t('general.archived'),
        icon: 'lucide:archive',
        info: $i18n.t('customer.filters.archivedInfo'),
        component: 'RadioGroupField',
        strategy: 'hybrid',
        options: [
          { label: $i18n.t('general.archived'), value: 'archived' },
          { label: $i18n.t('customer.filters.notArchived'), value: 'notArchived' },
        ],
        props: { outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2' },
        defaultValue: 'notArchived',
        isEmpty: () => false,
        valueLabel: (value) => {
          if (value === 'all') return $i18n.t('general.all');
          if (value === 'archived') return $i18n.t('general.yes');
          return null;
        },
        matches: (customers) => customers,
        filterRemotely: (value) => {
          let remotes = 0;
          if (configs) {
            for (const key in configs) {
              if (key === 'archived') continue;
              const cfg = configs[key];
              if (
                cfg &&
                (cfg.strategy === 'remote' || cfg.strategy === 'hybrid') &&
                states?.value?.[key] &&
                !cfg.isEmpty(states.value[key].value)
              ) {
                remotes++;
              }
            }
          }
          return remotes > 0 || value === 'all' || value === 'archived';
        },
        filterLocally: (value) => {
          let remotes = 0;
          if (configs) {
            for (const key in configs) {
              if (key === 'archived') continue;
              const cfg = configs[key];
              if (
                cfg &&
                (cfg.strategy === 'remote' || cfg.strategy === 'hybrid') &&
                states?.value?.[key] &&
                !cfg.isEmpty(states.value[key].value)
              ) {
                remotes++;
              }
            }
          }
          return remotes === 0 && value === 'notArchived';
        },
        getParams: (value) => {
          if (value === 'all') return { archived: null };
          if (value === 'archived') return { archived: true };
          return { archived: false };
        },
      },

      giftCards: {
        type: 'giftCards',
        label: $i18n.t('general.giftCards'),
        icon: 'lucide:gift',
        info: $i18n.t('customer.filters.giftCardInfo'),
        component: 'RadioGroupField',
        strategy: 'remote',
        options: [
          { label: $i18n.t('customer.filters.valid'), value: 'valid' },
          { label: $i18n.t('general.any'), value: 'any' },
        ],
        props: { outline: true, baseClass: 'flex rounded-lg p-1 h-9 focus-within:ring-2' },
        defaultValue: null,
        isEmpty: (value) => !value,
        valueLabel: (value) => (value === 'valid' ? $i18n.t('customer.filters.valid') : $i18n.t('general.any')),
        getParams: (value) => (value === 'valid' ? { valid: true } : {}),
      },

      formsFilled: {
        type: 'forms',
        label: $i18n.t('customer.filters.formsFilled'),
        icon: 'lucide:file-check', // TODO confirm icon
        info: $i18n.t('customer.filters.formsFilledInfo'),
        // `kind: 'list'` → FacetedFilter renders its built-in
        // ListPicker (searchable, multi-select). The `forms` options
        // hydrate via `updateFormOptions()` after the store loads.
        kind: 'list',
        strategy: 'remote',
        defaultValue: [],
        isEmpty: (value) => !Array.isArray(value) || value.length === 0,
        valueLabel: (value) => {
          if (!Array.isArray(value) || !value.length) return null;
          return constructLabel(value, 'title', $i18n);
        },
        getParams: (value) => ({ forms: value.map(({ idForm }: any) => idForm) }),
      },

      formsRequested: {
        type: 'requested-forms',
        label: $i18n.t('customer.filters.formsRequested'),
        icon: 'lucide:file-clock', // TODO confirm icon
        info: $i18n.t('customer.filters.formsRequestedInfo'),
        // `kind: 'list'` → FacetedFilter renders its built-in
        // ListPicker (searchable, multi-select). The `forms` options
        // hydrate via `updateFormOptions()` after the store loads.
        kind: 'list',
        strategy: 'remote',
        defaultValue: [],
        isEmpty: (value) => !Array.isArray(value) || value.length === 0,
        valueLabel: (value) => {
          if (!Array.isArray(value) || !value.length) return null;
          return constructLabel(value, 'title', $i18n);
        },
        getParams: (value) => ({ forms: value.map(({ idForm }: any) => idForm) }),
      },

      formsNotRequested: {
        type: 'no-forms',
        label: $i18n.t('customer.filters.formsNotRequested'),
        icon: 'lucide:file-x', // TODO confirm icon
        info: $i18n.t('customer.filters.formsNotRequestedInfo'),
        // `kind: 'list'` → FacetedFilter renders its built-in
        // ListPicker (searchable, multi-select). The `forms` options
        // hydrate via `updateFormOptions()` after the store loads.
        kind: 'list',
        strategy: 'remote',
        defaultValue: [],
        isEmpty: (value) => !Array.isArray(value) || value.length === 0,
        valueLabel: (value) => {
          if (!Array.isArray(value) || !value.length) return null;
          return constructLabel(value, 'title', $i18n);
        },
        getParams: (value) => ({ forms: value.map(({ idForm }: any) => idForm) }),
      },

      products: {
        type: 'products',
        label: $i18n.t('general.products'),
        icon: 'lucide:package', // TODO confirm icon
        info: $i18n.t('customer.filters.productsInfo'),
        component: 'ProductsFilter',
        strategy: 'remote',
        watch: ['date'],
        options: {
          products: {
            key: 'products',
            component: 'CheckboxCategoriesField',
            label: $i18n.t('general.products'),
            visible: false,
            search: true,
            selectCategory: true,
            valueLabel: () => {
              const value = states?.value?.products?.value?.products || [];
              if (value.length) return constructLabel(value, 'title', $i18n);
              return $i18n.t('product.all');
            },
            items: [],
          },
        },
        defaultValue: { products: [], date: { start: null, finish: null } },
        isEmpty: (value) => !value.date.start && !value.date.finish && !value.products.length,
        valueLabel: (value) => {
          const start = value.date.start ? new Date(value.date.start * 1000) : null;
          const finish = value.date.finish ? new Date(value.date.finish * 1000) : null;
          const date = start || finish
            ? `(${start ? $date.format(start, $date.presets.short) : ''} » ${finish ? $date.format(finish, $date.presets.short) : ''})`
            : '';
          const prodLabel = states?.value?.products?.options?.products?.valueLabel?.() ?? $i18n.t('product.all');
          return `${prodLabel} ${date}`;
        },
        getParams: (value) => ({
          products: value.products.map(({ idProduct }: any) => ({ id: idProduct })),
          from: value.date.start,
          to: value.date.finish,
        }),
      },

      giftCardsPurchased: {
        type: 'giftCardPurchased',
        label: $i18n.t('general.giftCards'),
        icon: 'lucide:gift', // TODO confirm icon
        info: $i18n.t('customer.filters.hasPurchasedGiftCard'),
        component: 'DateRangeFilter',
        strategy: 'remote',
        defaultValue: dateRangeDefaultValue(),
        isEmpty: dateRangeIsEmpty,
        valueLabel: (value) => dateRangeValueLabel(value, $date),
        getParams: dateRangeGetParams,
      },

      passes: {
        type: 'passes',
        label: $i18n.t('general.passes'),
        icon: 'lucide:ticket', // TODO confirm icon
        info: $i18n.t('customer.filters.hasPurchasedSelectedPasses'),
        component: 'PassesFilter',
        features: ['passes'],
        strategy: 'remote',
        watch: ['date'],
        options: {
          passes: {
            key: 'passes',
            component: 'CheckboxGroupField',
            label: $i18n.t('general.passes'),
            visible: false,
            search: true,
            selectCategory: true,
            valueLabel: () => {
              const value = states?.value?.passes?.value?.passes || [];
              if (value.length) {
                const labels = value.slice(0, 3).map(({ name }: any) => name).join($i18n.t('general.listSeparator'));
                return labels + (value.length > 3 ? ` and ${value.length - 3} more` : '');
              }
              return $i18n.t('customer.filters.allPasses');
            },
            items: [],
          },
        },
        defaultValue: { passes: [], date: { start: null, finish: null } },
        isEmpty: (value) => !value.date.start && !value.date.finish && !value.passes.length,
        valueLabel: (value) => {
          const start = value.date.start ? new Date(value.date.start * 1000) : null;
          const finish = value.date.finish ? new Date(value.date.finish * 1000) : null;
          const date = start || finish
            ? `(${start ? $date.format(start, $date.presets.short) : ''} » ${finish ? $date.format(finish, $date.presets.short) : ''})`
            : '';
          const passLabel = states?.value?.passes?.options?.passes?.valueLabel?.() ?? $i18n.t('customer.filters.allPasses');
          return `${passLabel} ${date}`;
        },
        getParams: (value) => ({
          passes: value.passes.map(({ idPass }: any) => ({ id: idPass })),
          from: value.date.start,
          to: value.date.finish,
        }),
      },
    } as Record<string, FilterConfig<Customer>>,

    // --- Categories ---

    categories: [
      { label: $i18n.t('customer.filters.personal'), filters: ['hasEmail', 'hasPhone', 'gender', 'dateOfBirth', 'hasPhoto', 'uploads'] },
      { label: $i18n.t('general.appointments'), filters: ['services', 'inactive', 'active', 'noShows', 'cancellations'] },
      { label: $i18n.t('general.segments'), filters: ['segments'], features: ['segments'] },
      { label: $i18n.t('general.marketing'), filters: ['loyaltyPoints', 'giftCards', 'allowOnlineBooking', 'privacyConsent', 'marketingConsent', 'massEmailSubscription', 'massSMSSubscription', 'emailBounced'] },
      { label: $i18n.t('general.forms'), filters: ['formsFilled', 'formsRequested', 'formsNotRequested'], features: ['forms'] },
      { label: $i18n.t('general.products'), filters: ['products', 'giftCardsPurchased', 'passes'] },
      { label: $i18n.t('general.customFields'), filters: [], features: ['custom-fields'] },
      { label: $i18n.t('general.manage'), filters: ['duplicate', 'archived', 'created'] },
    ],

    categorySets: {
      marketing: [
        { label: $i18n.t('customer.filters.personal'), filters: ['hasEmail', 'hasPhone', 'gender', 'dateOfBirth', 'hasPhoto', 'uploads'] },
        { label: $i18n.t('general.appointments'), filters: ['services', 'inactive', 'active', 'noShows', 'cancellations'] },
        { label: $i18n.t('general.segments'), filters: ['segments'], features: ['segments'] },
        { label: $i18n.t('general.marketing'), filters: ['loyaltyPoints', 'giftCards', 'allowOnlineBooking', 'privacyConsent', 'marketingConsent', 'massEmailSubscription', 'massSMSSubscription'] },
        { label: $i18n.t('general.forms'), filters: ['formsFilled', 'formsRequested', 'formsNotRequested'], features: ['forms'] },
        { label: $i18n.t('general.products'), filters: ['products', 'giftCardsPurchased', 'passes'] },
        { label: $i18n.t('general.customFields'), filters: [], features: ['custom-fields'] },
        { label: $i18n.t('general.manage'), filters: ['duplicate', 'created'] },
      ],
    },
  });

  // Assign deferred references now that the controller is created
  states = controller.filterStates;
  configs = controller.filterConfigs;

  // --- Filter Options (store-based) ---
  // Set up watchers to update filter options when stores change

  const formStore = useFormStore();
  const employeeStore = useEmployeeStore();
  const serviceStore = useServiceStore();
  const segmentStore = useSegmentStore();
  const productStore = useProductStore();

  const updateFormOptions = () => {
    const options = Object.values(formStore.all).map((form: any) => ({
      label: form.title,
      value: form,
      description: '#' + form.idForm,
      idForm: form.idForm,
      title: form.title,
    }));
    if (states.value.formsFilled) states.value.formsFilled.options = options;
    if (states.value.formsRequested) states.value.formsRequested.options = options;
    if (states.value.formsNotRequested) states.value.formsNotRequested.options = options;
  };

  const updateEmployeeOptions = () => {
    const options = Object.values(employeeStore.all)
      .filter((emp: any) => emp.type === EmployeeType.Employee)
      .map((employee: any) => ({
        value: employee,
        text: employee.displayName,
        info: employee.type != null ? $i18n.t(EmployeeTypeLabels[employee.type as EmployeeType]) : $i18n.t('general.employee'),
        image: employee.images ? employee.images.square : null,
      }));
    if (states.value.employees?.options?.employees) {
      states.value.employees.options.employees.items = options;
    }
    if (states.value.services?.options?.employees) {
      states.value.services.options.employees.items = options;
    }
  };

  const updateServiceOptions = () => {
    const options = Object.values(serviceStore.categorised).map((category: any) => ({
      label: category.name || '',
      options: category.services?.map((service: any) => ({
        value: service,
        text: service.title,
      })),
    }));
    if (states.value.services?.options?.services) {
      states.value.services.options.services.items = options;
    }
  };

  const updateSegmentOptions = () => {
    const options = Object.values(segmentStore.all).map((segment: any) => ({
      value: segment,
      label: segment.name,
      text: segment.name,
      colour: segment.hexColour,
    }));
    if (states.value.segments) states.value.segments.options = options;
  };

  const updateProductOptions = () => {
    const products = productStore.all;
    const grouped: Record<string, { label: string; options: Array<{ value: any; text: string }> }> = {};

    const appendProduct = (product: any, fallbackCategory?: any) => {
      if (!product) {
        return;
      }

      const category = product.category || product.productCategory || fallbackCategory || null;
      const key = category?.idProductCategory?.toString?.() || category?.name || 'other';

      if (!grouped[key]) {
        grouped[key] = {
          label: category?.name || $i18n.t('general.other'),
          options: [],
        };
      }

      grouped[key].options.push({
        value: product,
        text: product.name || product.title || '-',
      });
    };

    if (products) {
      Object.values(products).forEach((entry: any) => {
        if (Array.isArray(entry?.products)) {
          entry.products.forEach((product: any) => appendProduct(product, entry.category || entry.productCategory));
          return;
        }

        appendProduct(entry);
      });
    }

    const options = Object.values(grouped)
      .map((group) => ({
        label: group.label,
        options: group.options,
      }))
      .filter((group) => group.options.length > 0);

    if (states.value.products?.options?.products) {
      states.value.products.options.products.items = options;
    }
  };

  const updatePassOptions = async () => {
    try {
      if ($features && !$features.get('passes')) {
        return;
      }

      const passes = await $api.pass.listAll();
      const options = passes.map((pass: any) => ({
        value: pass,
        text: pass.name,
      }));
      if (states.value.passes?.options?.passes) {
        states.value.passes.options.passes.items = options;
      }
    } catch {
      // Continue with empty options
    }
  };

  // Initialize options
  updateFormOptions();
  updateEmployeeOptions();
  updateServiceOptions();
  updateSegmentOptions();
  updateProductOptions();
  updatePassOptions();

  // Expose update functions on the controller for external watchers
  const optionUpdaters = {
    updateFormOptions,
    updateEmployeeOptions,
    updateServiceOptions,
    updateSegmentOptions,
    updateProductOptions,
    updatePassOptions,
  };

  return {
    ...controller,
    ...optionUpdaters,
  } as CustomerFilterReturn;
};
